// Builds the response payload entries for save/submit, shared across the assessment
// flows. Field names differ per flow (e.g. `enterpriseAssessmentResponseAnswer` vs
// `tprmAssessmentResponseAnswer`), so callers pass a `buildEntry` shaping function.

// Every dirty question produces an entry — including questions with no answer and no
// files. A question can be dirty solely because it was flagged, un-flagged, or annotated,
// and skipping it would silently drop the flag/note (or fail to persist an un-flag).
export function buildDirtyResponseEntries({
    dirtyQuestionIds,
    questions,
    getQuestionId,
    responses = {},
    notes = {},
    flaggedQuestions = new Set(),
    buildEntry,
}) {
    return Array.from(dirtyQuestionIds || []).map(qIdStr => {
        const q = (questions || []).find(x => String(getQuestionId(x)) === qIdStr) || null;
        return buildEntry({
            question: q,
            questionId: q ? getQuestionId(q) : parseInt(qIdStr, 10),
            answer: responses[qIdStr] || '',
            note: notes[qIdStr] || '',
            isFlagged: flaggedQuestions.has(qIdStr),
        });
    });
}

// Saved (server-persisted) documents of questions a parent answer just hid. The page drops them
// from view at once, but they are queued rather than deleted: nothing else about the hide is
// stored until the next save (on the all-questions page, not until submit), and a document is
// linked to its question's stored response row, which a hide-reveal-re-answer before any save
// updates rather than replaces — so without a delete the document reappears once the assessment
// is reopened. Returns a new queue keyed by question id.
export function queueHiddenSavedDocuments(queue, hiddenQuestionIds, savedDocuments) {
    const next = { ...queue };
    hiddenQuestionIds.forEach(qId => {
        const key = String(qId);
        const docs = (savedDocuments[key] || []).filter(d => d?.id);
        if (docs.length === 0) return;
        const queuedIds = new Set((next[key] || []).map(d => d.id));
        next[key] = [...(next[key] || []), ...docs.filter(d => !queuedIds.has(d.id))];
    });
    return next;
}

// Deletes every queued document after a successful response save. A 404 counts as done (the
// document is already gone); any other failure stays queued so the next save retries it.
// Returns the queue of documents that still need deleting.
export async function deleteQueuedDocuments(queue, deleteDocument) {
    const remaining = {};
    await Promise.all(Object.entries(queue).flatMap(([qId, docs]) =>
        docs.map(async doc => {
            try {
                await deleteDocument(doc);
            } catch (err) {
                if (err?.response?.status === 404) return;
                (remaining[qId] ||= []).push(doc);
            }
        })));
    return remaining;
}

// Takes queued documents out of the queue before they are deleted, so documents handleResponse
// queues while those deletes are in flight stay queued. `include(questionId)` limits it to some
// questions' entries (all when omitted). Returns `{ taken, rest }`.
export function takeQueuedDocuments(queue, include) {
    const taken = {};
    const rest = {};
    Object.entries(queue).forEach(([qId, docs]) => {
        if (!include || include(qId)) taken[qId] = docs; else rest[qId] = docs;
    });
    return { taken, rest };
}

// Puts documents back in the queue (the ones deleteQueuedDocuments could not delete), skipping
// any already queued. Returns a new queue.
export function mergeQueuedDocuments(queue, docsByQuestion) {
    const next = { ...queue };
    Object.entries(docsByQuestion).forEach(([qId, docs]) => {
        const queuedIds = new Set((next[qId] || []).map(d => d.id));
        const added = docs.filter(d => !queuedIds.has(d.id));
        if (added.length > 0) next[qId] = [...(next[qId] || []), ...added];
    });
    return next;
}

// Records `ownerId` as the question whose answer hid `hiddenIds` (a per-question save deletes only
// the queued documents its question owns). Queued questions owned by one of `hiddenIds` move to
// `ownerId` too: a follow-up hidden earlier by a child that is now hidden itself would otherwise
// stay with a question that never saves again, and wait for Submit. Returns a new owner map.
export function assignHiddenDocumentOwner(owners, ownerId, hiddenIds) {
    const owner = String(ownerId);
    const hidden = new Set(hiddenIds.map(String));
    const next = {};
    Object.entries(owners).forEach(([qId, current]) => {
        next[qId] = hidden.has(current) ? owner : current;
    });
    hidden.forEach(qId => { next[qId] = owner; });
    return next;
}
